import { Controller } from "@hotwired/stimulus"

// Preserves the calendar's vertical scroll position across week
// navigation (prev/next clicks) AND full page navigations. Without
// this, every navigation resets the scroll container to 0 — a
// practitioner viewing the afternoon has to scroll past 8am every
// time they change weeks.
//
// Strategy: on every scroll event, write the current scrollTop to
// sessionStorage. On connect (including after a Turbo Frame swap),
// read the saved value and restore it. We restore via
// requestAnimationFrame so we win the race against any sibling
// controller (e.g. calendar_controller) that sets a default scroll
// position from in-memory state during its own connect.
//
// Key is fixed (not tied to week) — the user's preferred scroll
// position should persist regardless of which week they're viewing.
export default class extends Controller {
  static values = {
    storageKey: { type: String, default: "calendar-scroll-position" }
  }

  connect() {
    this.onScroll = this.onScroll.bind(this)
    this.element.addEventListener("scroll", this.onScroll, { passive: true })
    this.restore()
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.onScroll)
    this.save()
  }

  onScroll() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => {
      this.save()
      this.rafId = null
    })
  }

  save() {
    try {
      sessionStorage.setItem(this.storageKeyValue, String(this.element.scrollTop))
    } catch (e) {}
  }

  restore() {
    try {
      const raw = sessionStorage.getItem(this.storageKeyValue)
      if (raw == null) return
      const value = parseInt(raw, 10)
      if (!Number.isFinite(value) || value <= 0) return

      // Defer to next frame so we win the race against sibling
      // controllers that may also assign scrollTop on connect.
      requestAnimationFrame(() => {
        this.element.scrollTop = value
      })
    } catch (e) {}
  }
}
